import { type VariantProps, cva } from "class-variance-authority";
import DOMPurify from "dompurify";

import { Show, createMemo, createSignal, splitProps } from "solid-js";
import type { JSX } from "solid-js/jsx-runtime";

import Icon from "@/components/Icon.tsx";
import { inputSize } from "@/hooks/usePublic.ts";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile.ts";
import { parseRegexString } from "@/lib/validateUtils.ts";

import { cn } from "../../../lib/tailwindcssTool.ts";
import "./Input.css";

export const inputVariants = cva("", {
  variants: {
    size: {
      default: "base-input-default-size",
      sm: "base-input-sm",
      lg: "base-input-lg",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

type BaseInputProps = {
  id?: string;
  name?: string;
  value?: string | number;
  placeholder?: string;
  IsFloatPlaceholder?: boolean;
  disabled?: boolean;
  required?: boolean;
  containerClass?: string;
  class?: string;
  inputClass?: string;
  error?: string;
  tips?: string;
  prefix?: JSX.Element;
  suffix?: JSX.Element;
  success?: boolean;
  maxLength?: number;
  showClose?: boolean;
  noAutoFill?: boolean;
  noDefaultBorder?: boolean;
  lock?: boolean;
  onChange?: JSX.ChangeEventHandlerUnion<HTMLInputElement, Event>;
  onInput?: JSX.ChangeEventHandlerUnion<HTMLInputElement, Event>;
  onBlur?: JSX.FocusEventHandlerUnion<HTMLInputElement, FocusEvent>;
  onFocus?: JSX.FocusEventHandlerUnion<HTMLInputElement, FocusEvent>;
  onKeyDown?: (e: KeyboardEvent) => void;
  onClose?: () => void;
  valueRegExp?: string;
  disableFloatLabel?: boolean;
} & VariantProps<typeof inputVariants>;

export type InputProps =
  | (BaseInputProps & {
      type?: "text" | "email" | "number" | "tel";
    })
  | (BaseInputProps & {
      type: "password";
      showPasswordToggle?: boolean;
      onEyeClick?: () => void;
    });

export function Input(props: InputProps) {
  const [local, variants, others] = splitProps(
    props,
    [
      "class",
      "type",
      "error",
      "value",
      "success",
      "prefix",
      "suffix",
      "maxLength",
      "showClose",
      "onClose",
      "noAutoFill",
      "valueRegExp",
      "tips",
      "noDefaultBorder",
      "IsFloatPlaceholder",
      "disabled",
      "lock",
      "containerClass",
    ],
    ["size"],
  );

  let inputRef: HTMLInputElement | undefined;

  const [focused, setFocused] = createSignal(false);
  const [showPassword, setShowPassword] = createSignal(false);
  const [showTips, setShowTips] = createSignal(false);
  const [internalValue, setInternalValue] = createSignal(local.value || "");
  const sanitizedValue = createMemo(() =>
    DOMPurify.sanitize((local.value as string) || ""),
  );

  const hasValue = createMemo(() => {
    return Boolean(sanitizedValue() || internalValue());
  });

  const isPassword = local.type === "password";
  const passwordProps = props as Extract<InputProps, { type: "password" }>;
  const inputAutoFilling = {
    autocapitalize: "off" as const,
    autocorrect: "off" as const,
    autocomplete: "off" as const,
  };

  const inputPlaceholder = createMemo(() => {
    return focused() || hasValue() ? "" : props.placeholder;
  });

  const shouldFloatLabel = createMemo(() => {
    if (props.disableFloatLabel) return false;
    return (
      (focused() || hasValue()) &&
      props.placeholder &&
      (local.IsFloatPlaceholder ?? true)
    );
  });

  return (
    <div class={local.containerClass}>
      <div
        class={cn(
          "flex h-[48px] w-full cursor-text items-center gap-[8px] rounded-[12px] border-[1px] border-transparent bg-[var(--colors-surface-bg-8)] py-[8px] pl-[16px] pr-[8px] transition duration-200 ease-in-out",
          focused()
            ? "border-[var(--colors-btn-border-default)]"
            : local.error && "!border-[var(--colors-border-wrong)]",
          local.class,
        )}
        onclick={() => inputRef?.focus()}
      >
        <Show when={local.prefix} fallback={<></>}>
          <div class="base-input-prefix" onClick={(e) => e.stopPropagation()}>
            {local.prefix}
          </div>
        </Show>
        <div class="flex flex-1 items-center gap-2">
          <input
            ref={(el) => (inputRef = el)}
            {...others}
            disabled={local.disabled || local.lock}
            type={
              isPassword
                ? showPassword()
                  ? "text"
                  : "password"
                : local.type || "text"
            }
            value={sanitizedValue()}
            class={cn(
              "font['Onest'] no-spinner flex-1 border-none bg-transparent text-[12px] text-[var(--colors-text-primary)] placeholder:text-[var(--colors-text-secondary)] focus:outline-none",
              // shouldFloatLabel()
              //   ? "placeholder:opacity-0"
              //   : "placeholder:text-var(--colors-text-secondary)",
              props.inputClass ?? "",
              sanitizedValue() ? "font-bold" : "",
            )}
            placeholder={props.placeholder}
            {...(local.noAutoFill ? inputAutoFilling : {})}
            autocomplete="new-password"
            onFocus={(e) => {
              setFocused(true);
              if (typeof others.onFocus === "function") {
                others.onFocus(e);
              }
            }}
            onBlur={(e) => {
              setFocused(false);
              if (typeof others.onBlur === "function") {
                others.onBlur(e);
              }
            }}
            onInput={(e) => {
              setInternalValue(e.target.value);
              if (typeof others.onInput === "function") {
                if (local.valueRegExp) {
                  const regex = parseRegexString(local.valueRegExp);
                  if (regex) {
                    const value = e.target.value.replace(regex, "");
                    e.target.value = value;
                    e.target.innerHTML = value;
                  }
                }
                others.onInput(e);
              }
            }}
            onKeyDown={(e) => {
              if (typeof others.onKeyDown === "function") {
                others.onKeyDown(e);
              }
            }}
            maxLength={local.maxLength}
          />
          <Show
            when={isPassword && passwordProps.showPasswordToggle !== false}
            fallback={<></>}
          >
            <button
              type="button"
              class="base-input-password-toggle h-full cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowPassword(!showPassword());
                if (typeof passwordProps.onEyeClick === "function") {
                  passwordProps.onEyeClick();
                }
              }}
              tabIndex={-1}
            >
              <div class="flex items-center gap-[10px] rounded-[8px] bg-[var(--colors-surface-bg-9)] p-[10px]">
                <Show
                  when={showPassword()}
                  fallback={
                    <Icon
                      name="general/eye"
                      class="size-[16px]"
                      color="var(--colors-icon-tab-default)"
                    />
                  }
                >
                  <Icon
                    name="general/eye-1"
                    class="size-[16px]"
                    color="var(--colors-icon-tab-default)"
                  />
                </Show>
              </div>
            </button>
          </Show>
          <Show when={local.showClose && hasValue()} fallback={<></>}>
            <div class="flex size-7 cursor-pointer items-center justify-center rounded-[8px] bg-[var(--colors-surface-bg-9)]">
              <Icon
                name="cross"
                class={`text-general-icon size-[18px]`}
                onClick={() => {
                  setInternalValue("");
                  local.onClose?.();
                }}
              />
            </div>
          </Show>
          <Show when={local.lock} fallback={<></>}>
            <div class="flex size-7 cursor-pointer items-center justify-center rounded-[8px] bg-[var(--colors-surface-bg-9)]">
              <Icon
                name="general/lock"
                class={`text-general-icon size-[18px]`}
              />
            </div>
          </Show>
          <Show when={local.suffix && !isPassword} fallback={<></>}>
            <div class="base-input-suffix">{local.suffix}</div>
          </Show>
          <Show when={local.tips} fallback={<></>}>
            <div class="relative leading-[1]">
              <div
                class="relative"
                onMouseEnter={() => !isMobile() && setShowTips(true)}
                onMouseLeave={() => setShowTips(false)}
                onMouseDown={(ev) => ev.preventDefault()}
                onclick={(ev) => {
                  ev.preventDefault();
                  setShowTips(!showTips());
                }}
              >
                <Show when={showTips()} fallback={<></>}>
                  <div class="absolute right-[-8px] top-[-3px] z-[2] flex w-[max-content] max-w-[350px] translate-y-[-100%] flex-col items-end">
                    <div class="bg-general-bg7 text-general-light rounded-[5px] p-[10px] text-[11px] font-medium leading-[1.2]">
                      {local.tips}
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="10"
                      viewBox="0 0 12 10"
                      fill="none"
                      class="mr-[10px] shrink-0"
                    >
                      <path
                        d="M12 0H0L6.3923 10L12 0Z"
                        class="fill-general-bg7"
                      />
                    </svg>
                  </div>
                </Show>
                <Icon
                  name="general/gth"
                  class="size-[18px]"
                  color={showTips() ? "#FFFFFF" : "#B3B8C1"}
                />
              </div>
            </div>
          </Show>
        </div>
      </div>

      <Show when={local.error && !focused()} fallback={<></>}>
        <p
          class={`mt-[4px] text-[var(--colors-text-wrong)] ${isMobile() ? "text-[12px]" : "text-[14px]"}`}
        >
          {local.error}
        </p>
      </Show>

      <Show when={isPassword && focused()} fallback={<></>}>
        <p
          class={`base-input-password-tip-message font-medium ${isMobile() ? "text-[12px]" : "text-[14px]"}`}
        >
          {t("auth.characters", {
            val: "6-30",
          })}
        </p>
      </Show>
    </div>
  );
}
